import { Booking, Resource } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface DeleteBookingConfirmDialogProps {
  open: boolean;
  booking: Booking | null;
  resource?: Resource | null;
  onConfirm: () => void;
  onCancel: () => void;
  /** For recurring bookings: delete only this occurrence */
  onDeleteOnlyThis?: () => void;
  /** For recurring bookings: delete this and all future occurrences */
  onDeleteAllFuture?: () => void;
}

export function DeleteBookingConfirmDialog({
  open,
  booking,
  resource,
  onConfirm,
  onCancel,
  onDeleteOnlyThis,
  onDeleteAllFuture,
}: DeleteBookingConfirmDialogProps) {
  if (!booking) {
    return null;
  }

  const isRecurring = !!booking.recurrenceGroupId;

  // Recurring booking delete dialog with three options
  if (isRecurring && onDeleteOnlyThis && onDeleteAllFuture) {
    return (
      <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Recurring Booking</AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to delete this occurrence of the booking, or this and all future occurrences?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={onDeleteOnlyThis}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Only this booking
            </AlertDialogAction>
            <AlertDialogAction
              onClick={onDeleteAllFuture}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              All future booking
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  // Standard (non-recurring) delete dialog
  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Booking</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the booking for{' '}
            <span className="font-semibold text-foreground">
              &quot;{booking.projectName || booking.description || 'Booking'}&quot;
            </span>
            {resource?.name && (
              <>
                {' '}assigned to{' '}
                <span className="font-semibold text-foreground">
                  {resource.name}
                </span>
              </>
            )}
            ? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onConfirm}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            Delete Booking
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
